import { IPublicClientApplication } from "@azure/msal-browser";
import { graphPhotoRequest } from "./authConfig";

/**
 * Fetches the user's profile photo from Microsoft Graph API
 * @param instance - MSAL instance
 * @returns Base64 encoded photo URL or null if not available
 */
export async function getUserPhoto(instance: IPublicClientApplication): Promise<string | null> {
  try {
    const account = instance.getAllAccounts()[0];
    if (!account) {
      console.warn("No account found for photo fetch");
      return null;
    }

    // Acquire token silently
    const response = await instance.acquireTokenSilent({
      ...graphPhotoRequest,
      account: account
    });

    // Fetch the photo from Microsoft Graph
    const photoResponse = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
      headers: {
        Authorization: `Bearer ${response.accessToken}`
      }
    });

    if (!photoResponse.ok) {
      if (photoResponse.status === 404) {
        console.log("User has no profile photo");
      } else {
        console.error("Error fetching photo:", photoResponse.status);
      }
      return null;
    }

    // Convert the blob to base64
    const blob = await photoResponse.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error getting user photo:", error);
    return null;
  }
}

/**
 * Fetches the user's profile photo for Teams context (using existing token)
 * @param token - Teams SSO token
 * @returns Base64 encoded photo URL or null if not available
 */
export async function getUserPhotoWithToken(token: string): Promise<string | null> {
  try {
    // Fetch the photo from Microsoft Graph
    const photoResponse = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!photoResponse.ok) {
      if (photoResponse.status === 404) {
        console.log("User has no profile photo");
      } else {
        console.error("Error fetching photo:", photoResponse.status);
      }
      return null;
    }

    // Convert the blob to base64
    const blob = await photoResponse.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error getting user photo with token:", error);
    return null;
  }
}
